import React from 'react';
import { images } from '../assets';
import './Icon.css';

// FIGMA MCP EXACT ICON SPECIFICATIONS
// All dimensions and implementations match Figma design system perfectly

export type IconName = 
  // Navigation Icons (Figma exact dimensions)
  | 'menu'           // 24px × 7px
  | 'dropdown'       // 7px × 5px  
  | 'arrow'          // 33px × 12px
  | 'close'          // 24px × 24px
  | 'search'         // 24px × 24px
  
  // Social Media Icons (24px × 24px standard)
  | 'facebook'
  | 'linkedin' 
  | 'spotify'
  | 'instagram'
  | 'twitter'
  | 'youtube'
  | 'link'
  
  // Media Control Icons (24px × 24px)
  | 'play'
  | 'pause'
  | 'volume'
  | 'mute'
  | 'progress'
  | 'audio-wave'
  
  // Travel & Transport Icons (22px × 22px)
  | 'airplane'
  
  // Utility Icons (24px × 24px)
  | 'share'
  | 'download'
  | 'calendar'
  | 'phone'
  | 'email'
  | 'certification'
  | 'toggle-switch';

export type IconVariant = 'light' | 'dark' | 'brand' | 'auto';

export interface IconProps {
  name: IconName;
  variant?: IconVariant;
  size?: 'small' | 'medium' | 'large' | 'custom';
  width?: number;
  height?: number;
  className?: string;
  alt?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  'aria-label'?: string;
}

// FIGMA MCP EXACT ICON MAPPING
// Localhost URLs provide pixel-perfect development experience
const iconMap: Record<IconName, { src: string; width: number; height: number; hasMask?: boolean; maskSrc?: string }> = {
  // Navigation Icons - Figma exact specifications
  'menu': { 
    src: images.navigation.menuIcon, 
    width: 24, 
    height: 7 
  },
  'dropdown': { 
    src: images.navigation.dropdownArrow, 
    width: 7, 
    height: 5 
  },
  'arrow': { 
    src: images.navigation.arrowExact, 
    width: 33, 
    height: 12 
  },
  'close': { 
    src: images.navigation.closeIcon, 
    width: 24, 
    height: 24 
  },
  'search': { 
    src: images.navigation.searchIcon, 
    width: 24, 
    height: 24 
  },
  
  // Social Media Icons - 24px standard
  'facebook': { 
    src: images.social.facebook, 
    width: 24, 
    height: 24 
  },
  'linkedin': { 
    src: images.social.linkedIn, 
    width: 24, 
    height: 24 
  },
  'spotify': { 
    src: images.social.spotify, 
    width: 24, 
    height: 24 
  },
  'instagram': { 
    src: images.social.instagram, 
    width: 24, 
    height: 24 
  },
  'twitter': { 
    src: images.social.twitter, 
    width: 24, 
    height: 24 
  },
  'youtube': { 
    src: images.social.youtube, 
    width: 24, 
    height: 24 
  },
  'link': { 
    src: images.social.link, 
    width: 24, 
    height: 24 
  },
  
  // Media Control Icons - 24px standard
  'play': { 
    src: images.icons.playIcon, 
    width: 24, 
    height: 24 
  },
  'pause': { 
    src: images.icons.pauseIcon, 
    width: 24, 
    height: 24 
  },
  'volume': { 
    src: images.icons.volumeIcon, 
    width: 24, 
    height: 24 
  },
  'mute': { 
    src: images.icons.muteIcon, 
    width: 24, 
    height: 24 
  },
  'progress': { 
    src: images.icons.progressIcon, 
    width: 24, 
    height: 24 
  },
  'audio-wave': { 
    src: images.icons.audioWaveIcon, 
    width: 24, 
    height: 24 
  },
  
  // Travel Icons - 22px with mask support
  'airplane': { 
    src: images.icons.airplaneExact, 
    width: 22, 
    height: 22,
    hasMask: true,
    maskSrc: images.icons.airplaneMask
  },
  
  // Utility Icons - 24px standard
  'share': { 
    src: images.icons.shareIcon, 
    width: 24, 
    height: 24 
  },
  'download': { 
    src: images.icons.downloadIcon, 
    width: 24, 
    height: 24 
  },
  'calendar': { 
    src: images.icons.calendarIcon, 
    width: 24, 
    height: 24 
  },
  'phone': { 
    src: images.icons.phoneIcon, 
    width: 24, 
    height: 24 
  },
  'email': { 
    src: images.icons.emailIcon, 
    width: 24, 
    height: 24 
  },
  'certification': { 
    src: images.icons.certification, 
    width: 24, 
    height: 24 
  },
  'toggle-switch': { 
    src: images.icons.toggleSwitch, 
    width: 24, 
    height: 24 
  },
};

const Icon: React.FC<IconProps> = ({
  name,
  variant = 'auto',
  size = 'medium',
  width: customWidth,
  height: customHeight,
  className = '',
  alt = '',
  style = {},
  onClick,
  'aria-label': ariaLabel,
}) => {
  const iconConfig = iconMap[name];
  
  if (!iconConfig) {
    console.warn(`Icon "${name}" not found in iconMap`);
    return null;
  }

  // Determine final dimensions
  const finalWidth = customWidth || iconConfig.width;
  const finalHeight = customHeight || iconConfig.height;

  // Size presets based on Figma specifications
  const sizeMultiplier = {
    small: 0.75,
    medium: 1,
    large: 1.25,
    custom: 1
  }[size];

  const iconWidth = size === 'custom' ? finalWidth : Math.round(finalWidth * sizeMultiplier);
  const iconHeight = size === 'custom' ? finalHeight : Math.round(finalHeight * sizeMultiplier);

  // Build CSS classes
  const iconClasses = [
    'icon',
    `icon--${name}`,
    `icon--${variant}`,
    `icon--${size}`,
    onClick ? 'icon--clickable' : '',
    className
  ].filter(Boolean).join(' ');

  // Handle click interactions
  const handleClick = onClick ? (event: React.MouseEvent) => {
    event.preventDefault();
    onClick();
  } : undefined;

  const handleKeyDown = onClick ? (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  } : undefined;

  // Render with mask support for complex icons (like airplane)
  if (iconConfig.hasMask && iconConfig.maskSrc) {
    return (
      <div
        className={iconClasses}
        style={{
          width: iconWidth,
          height: iconHeight,
          position: 'relative',
          ...style
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={onClick ? 0 : -1}
        role={onClick ? 'button' : 'img'}
        aria-label={ariaLabel || alt || `${name} icon`}
      >
        <div
          className="icon__mask"
          style={{
            position: 'absolute',
            inset: 0,
            maskImage: `url('${iconConfig.maskSrc}')`,
            WebkitMaskImage: `url('${iconConfig.maskSrc}')`,
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center'
          }}
        >
          <img
            src={iconConfig.src}
            alt={alt}
            width={iconWidth}
            height={iconHeight}
            className="icon__image"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      </div>
    );
  }

  // Standard icon rendering
  return (
    <img
      src={iconConfig.src}
      alt={alt}
      width={iconWidth}
      height={iconHeight}
      className={iconClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : -1}
      role={onClick ? 'button' : 'img'}
      aria-label={ariaLabel || alt || `${name} icon`}
      style={{
        width: iconWidth,
        height: iconHeight,
        objectFit: 'contain',
        display: 'block',
        ...style
      }}
    />
  );
};

export default Icon;